import { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import CookieConsent from 'react-cookie-consent';
import '@/i18n';
import { AuthProvider } from '@/contexts/AuthContext';
import { SettingsProvider } from '@/contexts/SettingsContext';
import HomePage from '@/pages/HomePage';
import RoutesPage from '@/pages/RoutesPage';
import POIsPage from '@/pages/POIsPage';
import MapPage from '@/pages/MapPage';
import ProfilePage from '@/pages/ProfilePage';
import PricingPage from '@/pages/PricingPage';
import PaymentSuccessPage from '@/pages/PaymentSuccessPage';
import ChargingStationsPage from '@/pages/ChargingStationsPage';
import CommunityPage from '@/pages/CommunityPage';
import GuidePage from '@/pages/GuidePage';
import GuidedToursPageNew from '@/pages/GuidedToursPageNew';
import TourOperatorChecklistPage from '@/pages/TourOperatorChecklistPage';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import CreateRoutePage from '@/pages/CreateRoutePage';
import MyRoutesPage from '@/pages/MyRoutesPage';
import EventsPage from '@/pages/EventsPage';
import AdminEventsPage from '@/pages/AdminEventsPage';
import ImpressumPage from '@/pages/ImpressumPage';
import DatenschutzPage from '@/pages/DatenschutzPage';
import AGBPage from '@/pages/AGBPage';
import CookiePage from '@/pages/CookiePage';
import KnowledgePage from '@/pages/KnowledgePage';
import EquipmentGuide from '@/pages/EquipmentGuide';
import SafetyGuide from '@/pages/SafetyGuide';
import EBikeGuide from '@/pages/EBikeGuide';
import TrafficRulesGuide from '@/pages/TrafficRulesGuide';
import FahrtechnikGuide from '@/pages/FahrtechnikGuide';
import IslandsGuide from '@/pages/IslandsGuide';
import HighlandsGuide from '@/pages/HighlandsGuide';
import LowlandsGuide from '@/pages/LowlandsGuide';
import EdinburghFifeGuide from '@/pages/EdinburghFifeGuide';
import NC500Story from '@/pages/NC500Story';
import SkyeStory from '@/pages/SkyeStory';
import WhiskyTrailStory from '@/pages/WhiskyTrailStory';
import PacklistChecklist from '@/pages/PacklistChecklist';
import BikeCheckChecklist from '@/pages/BikeCheckChecklist';
import EmergencyChecklist from '@/pages/EmergencyChecklist';
import RouteDetailPage from '@/pages/RouteDetailPage';
import FeedbackPage from '@/pages/FeedbackPage';
import TourDetailPage from '@/pages/TourDetailPage';
import GalleryUploadPage from '@/pages/GalleryUploadPage';
import FirstAidGuidePage from '@/pages/FirstAidGuidePage';
import WildcampingRulesPage from '@/pages/WildcampingRulesPage';
import DeerStalkingPage from '@/pages/DeerStalkingPage';
import Footer from '@/components/Footer';
import NotificationBell from '@/components/NotificationBell';
import GlobalSearch from '@/components/GlobalSearch';
import OnboardingTour from '@/components/OnboardingTour';
import EmergencySOS from '@/components/EmergencySOS';
import { MapPin, Route as RouteIcon, Map, User, Globe, Zap, Users, BookOpen, LogOut, LogIn, Crown, Calendar, Settings, MessageSquare } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Navigation() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { user, logout, isPremium, isAdmin } = useAuth();

  const toggleLanguage = () => {
    const newLang = i18n.language === 'de' ? 'en' : 'de';
    i18n.changeLanguage(newLang);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="navigation" data-testid="main-navigation">
      <div className="nav-container">
        <Link to="/" className="nav-logo" data-testid="nav-logo">
          <img src="/logo.svg" alt="PedalScot Logo" className="logo-icon" />
          <span>{t('app_name')}</span>
        </Link>
        
        <GlobalSearch />
        
        <div className="nav-links">
          <Link 
            to="/routes" 
            className={`nav-link ${isActive('/routes') ? 'active' : ''}`}
            data-testid="nav-routes"
          >
            <RouteIcon size={20} />
            <span>{t('nav.routes')}</span>
          </Link>
          <Link 
            to="/pois" 
            className={`nav-link ${isActive('/pois') ? 'active' : ''}`}
            data-testid="nav-pois"
          >
            <MapPin size={20} />
            <span>{t('nav.pois')}</span>
          </Link>
          <Link 
            to="/charging" 
            className={`nav-link ${isActive('/charging') ? 'active' : ''}`}
            data-testid="nav-charging"
          >
            <Zap size={20} />
            <span>{t('nav.charging')}</span>
          </Link>
          <Link 
            to="/map" 
            className={`nav-link ${isActive('/map') ? 'active' : ''}`}
            data-testid="nav-map"
          >
            <Map size={20} />
            <span>{t('nav.map')}</span>
          </Link>
          <Link 
            to="/events" 
            className={`nav-link ${isActive('/events') ? 'active' : ''}`}
            data-testid="nav-events"
          >
            <Calendar size={20} />
            <span>Events</span>
          </Link>
          <Link 
            to="/wissen" 
            className={`nav-link ${isActive('/wissen') ? 'active' : ''}`}
            data-testid="nav-knowledge"
          >
            <BookOpen size={20} />
            <span>Wissen</span>
          </Link>
          <Link 
            to="/guided-tours" 
            className={`nav-link ${isActive('/guided-tours') ? 'active' : ''}`}
            data-testid="nav-guided-tours"
          >
            <Users size={20} />
            <span>Geführte Touren</span>
          </Link>
          <Link 
            to="/feedback" 
            className={`nav-link ${isActive('/feedback') ? 'active' : ''}`}
            data-testid="nav-feedback"
          >
            <MessageSquare size={20} />
            <span>Feedback</span>
          </Link>
          <Link 
            to="/guide" 
            className={`nav-link ${isActive('/guide') ? 'active' : ''}`}
            data-testid="nav-guide"
          >
            <BookOpen size={20} />
            <span>{t('nav.guide')}</span>
          </Link>
          <Link 
            to="/community" 
            className={`nav-link ${isActive('/community') ? 'active' : ''}`}
            data-testid="nav-community"
          >
            <Users size={20} />
            <span>{t('nav.community')}</span>
          </Link>
          {isPremium && (
            <Link 
              to="/my-routes" 
              className={`nav-link ${isActive('/my-routes') ? 'active' : ''}`}
              data-testid="nav-my-routes"
            >
              <RouteIcon size={20} />
              <span>Meine Routen</span>
              <Crown size={14} className="premium-indicator" />
            </Link>
          )}
          <Link 
            to="/profile" 
            className={`nav-link ${isActive('/profile') ? 'active' : ''}`}
            data-testid="nav-profile"
          >
            <User size={20} />
            <span>{t('nav.profile')}</span>
          </Link>
          {isAdmin && (
            <Link 
              to="/admin/events" 
              className={`nav-link ${isActive('/admin/events') ? 'active' : ''}`}
              data-testid="nav-admin"
            >
              <Settings size={20} />
              <span>Admin</span>
              <Crown size={14} className="premium-indicator" />
            </Link>
          )}
        </div>

        <div className="nav-actions">
          <button 
            onClick={toggleLanguage} 
            className="lang-toggle"
            data-testid="language-toggle"
          >
            <Globe size={20} />
            <span>{i18n.language.toUpperCase()}</span>
          </button>

          {user && <NotificationBell />}

          {user ? (
            <div className="user-menu">
              {isPremium && (
                <span className="premium-badge">
                  <Crown size={16} />
                  Premium
                </span>
              )}
              <span className="user-name">{user.name}</span>
              <button onClick={logout} className="logout-button">
                <LogOut size={18} />
                {t('auth.logout')}
              </button>
            </div>
          ) : (
            <Link to="/login" className="login-link">
              <LogIn size={18} />
              {t('auth.login')}
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
}

function App() {
  const [dataInitialized, setDataInitialized] = useState(false);

  useEffect(() => {
    const initData = async () => {
      try {
        await axios.post(`${API}/init-data`);
        setDataInitialized(true);
      } catch (error) {
        console.log('Data initialization:', error.response?.data?.message || 'Already initialized');
        setDataInitialized(true);
      }
    };
    initData();
  }, []);

  if (!dataInitialized) {
    return (
      <div className="loading-screen" data-testid="loading-screen">
        <div className="loading-spinner"></div>
        <p>PedalScot wird geladen...</p>
      </div>
    );
  }

  return (
    <div className="App" data-testid="app-container">
      <BrowserRouter>
        <SettingsProvider>
          <AuthProvider>
            <OnboardingTour />
            <Navigation />
            <main className="main-content">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/routes" element={<RoutesPage />} />
                <Route path="/routes/:id" element={<RouteDetailPage />} />
                <Route path="/pois" element={<POIsPage />} />
                <Route path="/charging" element={<ChargingStationsPage />} />
                <Route path="/map" element={<MapPage />} />
                <Route path="/guide" element={<GuidePage />} />
                <Route path="/community" element={<CommunityPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/payment-success" element={<PaymentSuccessPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/create-route" element={<CreateRoutePage />} />
                <Route path="/my-routes" element={<MyRoutesPage />} />
                <Route path="/events" element={<EventsPage />} />
                <Route path="/admin/events" element={<AdminEventsPage />} />
                <Route path="/impressum" element={<ImpressumPage />} />
                <Route path="/datenschutz" element={<DatenschutzPage />} />
                <Route path="/agb" element={<AGBPage />} />
                <Route path="/cookies" element={<CookiePage />} />
                <Route path="/wissen" element={<KnowledgePage />} />
                <Route path="/guided-tours" element={<GuidedToursPageNew />} />
                <Route path="/wissen/ausruestung" element={<EquipmentGuide />} />
                <Route path="/wissen/fahrtechnik" element={<FahrtechnikGuide />} />
                <Route path="/wissen/sicherheit" element={<SafetyGuide />} />
                <Route path="/wissen/ebike" element={<EBikeGuide />} />
                <Route path="/wissen/verkehrsregeln" element={<TrafficRulesGuide />} />
                <Route path="/wissen/region/islands" element={<IslandsGuide />} />
                <Route path="/wissen/region/highlands" element={<HighlandsGuide />} />
                <Route path="/wissen/region/lowlands" element={<LowlandsGuide />} />
                <Route path="/wissen/region/edinburgh" element={<EdinburghFifeGuide />} />
                <Route path="/wissen/story/nc500" element={<NC500Story />} />
                <Route path="/wissen/story/skye" element={<SkyeStory />} />
                <Route path="/wissen/story/whisky" element={<WhiskyTrailStory />} />
                <Route path="/wissen/checklist/packlist" element={<PacklistChecklist />} />
                <Route path="/wissen/checklist/bike" element={<BikeCheckChecklist />} />
                <Route path="/wissen/checklist/notfall" element={<EmergencyChecklist />} />
                <Route path="/knowledge/tour-operator-checklist" element={<TourOperatorChecklistPage />} />
                <Route path="/feedback" element={<FeedbackPage />} />
                <Route path="/tour/:id" element={<TourDetailPage />} />
                <Route path="/upload-gallery" element={<GalleryUploadPage />} />
                <Route path="/first-aid" element={<FirstAidGuidePage />} />
                <Route path="/wildcamping-rules" element={<WildcampingRulesPage />} />
                <Route path="/deer-stalking" element={<DeerStalkingPage />} />
              </Routes>
              <Footer />
            </main>
            
            {/* Emergency SOS Button - Always available */}
            <EmergencySOS />
          </AuthProvider>
        </SettingsProvider>
        
        {/* DSGVO Cookie Banner */}
        <CookieConsent
          location="bottom"
          buttonText="Alle akzeptieren"
          declineButtonText="Nur notwendige"
          enableDeclineButton
          cookieName="pedalscot_cookie_consent"
          style={{
            background: "#2c5f2d",
            padding: "15px 20px",
            alignItems: "center",
            fontSize: "14px"
          }}
          buttonStyle={{
            background: "#4a7c25",
            color: "white",
            fontSize: "13px",
            padding: "8px 20px",
            borderRadius: "6px",
            fontWeight: "600",
            margin: "0 5px"
          }}
          declineButtonStyle={{
            background: "transparent",
            border: "2px solid white",
            color: "white",
            fontSize: "13px",
            padding: "8px 20px",
            borderRadius: "6px",
            fontWeight: "600",
            margin: "0 5px"
          }}
          expires={365}
          onAccept={() => {
            console.log("Analytics cookies accepted");
            // Hier später Google Analytics aktivieren
          }}
          onDecline={() => {
            console.log("Only essential cookies");
          }}
        >
          <span style={{ fontSize: "13px", lineHeight: "1.4" }}>
            🍪 Cookies für bessere Nutzererfahrung.{" "}
            <Link to="/datenschutz" style={{ color: "#c8e6c9", textDecoration: "underline" }}>
              Mehr Info
            </Link>
          </span>
        </CookieConsent>
      </BrowserRouter>
    </div>
  );
}

export default App;
